import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { Check, ChevronsUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";

interface Job {
	id: string;
	title: string;
	company_name?: string;
}

interface JobSelectorProps {
	onSelectJob: (job: Job) => void;
	selectedJobId?: string;
	buttonClassName?: string;
	disabled?: boolean;
	companyId?: string;
}

export function JobSelector({
	onSelectJob,
	selectedJobId,
	buttonClassName,
	disabled = false,
	companyId
}: JobSelectorProps) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [jobs, setJobs] = useState<Job[]>([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	
	const selectedJob = jobs.find(job => job.id === selectedJobId);
	
	useEffect(() => {
		if (open) {
			fetchJobs();
		}
	}, [open, companyId, searchTerm]);
	
	const fetchJobs = async () => {
		setIsLoading(true);
		setError(null);
		
		try {
			let query = supabase
				.from('jobs')
				.select('id, title, company:company_id (name)');
				
			// Add search filter if there's a search term
			if (searchTerm) {
				query = query.ilike('title', `%${searchTerm}%`);
			} else {
				query = query.order('title', { ascending: true });
			}
			
			// If companyId is provided, filter jobs by that company
			if (companyId) {
				query = query.eq('company_id', companyId);
			}
			
			// Limit to a reasonable number
			query = query.limit(20);
			
			const { data, error } = await query;
			
			if (error) {
				throw error;
			}
			
			// Format the data to include company name when available
			const formattedJobs = (data as any[]).map(job => ({
				id: job.id,
				title: job.title,
				company_name: job.company ? job.company.name : undefined
			}));
			
			setJobs(formattedJobs);
			
		} catch (err) {
			console.error('Error fetching jobs:', err);
			setError(t("jobs.selector.error", "Failed to load jobs."));
		} finally {
			setIsLoading(false);
		}
	};
	
	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					disabled={disabled}
					role="combobox"
					aria-expanded={open}
					className={cn("justify-between", buttonClassName)}
				>
					{selectedJob?.title || t("jobs.selector.selectPlaceholder", "Select job...")}
					<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-[300px] p-0">
				<Command>
					<CommandInput 
						placeholder={t("jobs.selector.searchPlaceholder", "Search jobs...")} 
						value={searchTerm}
						onValueChange={setSearchTerm}
					/>
					<CommandList className="max-h-[300px]">
						{isLoading ? (
							<div className="py-6 text-center text-sm text-gray-500">
								{t("jobs.selector.loading", "Loading jobs...")}
							</div>
						) : error ? (
							<div className="py-6 text-center text-sm text-red-500">
								{error}
							</div>
						) : jobs.length === 0 ? (
							<CommandEmpty>{t("jobs.selector.noResults", "No jobs found.")}</CommandEmpty>
						) : (
							<CommandGroup>
								{jobs.map((job) => (
									<CommandItem
										key={job.id}
										value={job.id}
										onSelect={() => {
											onSelectJob(job);
											setOpen(false);
										}}
									>
										<Check
											className={cn(
												"mr-2 h-4 w-4",
												selectedJobId === job.id ? "opacity-100" : "opacity-0"
											)}
										/>
										<div className="flex flex-col">
											<span>{job.title}</span>
											{job.company_name && (
												<span className="text-xs text-gray-500">{job.company_name}</span>
											)}
										</div>
									</CommandItem>
								))}
							</CommandGroup>
						)}
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
